import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql } from 'gatsby';

import ChooseYourNumber from '../components/ChooseYourNumber';

import Auth from '../components/Auth';

import { authActions, userActions } from '@detox/actions';

import { ctSendEvent } from '../helpers/clicktale';
import { dtIdentifyUser } from '../helpers/dynatrace';
import getAemComponentByName from '../helpers/getAemComponentByName';
import isMobileSharePlan from '../helpers/is-mobile-share-plan';

import '../fragments/plan-detail';
import '../fragments/aem-components';
import { useSpecialPromo } from '../hooks/useSpecialPromo';
import { flattenNodes } from '../helpers/common';
import { useABTesting } from '@wec-core/ab-testing';
import { AbFeatureNames } from '../constants/ab-feature-names';
import { getCookie } from '@lux/helpers';
import CONSTANT from '../constants/common';
import { navigation } from '../middlewares/navigation-constants';

export const ChooseYourNumberPage = props => {
  const {
    data,
    authStatus,
    userInformation,
    cisInformation,
    setAuthStatus,
    selectedPlan,
    resetUser,
    userMyInfo,
    getUserInformation
  } = props;

  const { allAemPages, allPlan, allVariableConfig } = data;
  const [myInfoData, setMyInfoData] = useState(null);
  const isMyInfoLoading = Boolean(userMyInfo?.loading);
  const isUserInfoLoading = Boolean(userInformation?.loading);
  const plans = useMemo(() => flattenNodes(allPlan), [allPlan]);
  const aemVariableConfigs = flattenNodes(allVariableConfig) || [];
  const siteConfigurations = getAemComponentByName(
    allAemPages,
    'SiteConfigurations'
  );

  const isMobileShareFlow = isMobileSharePlan(selectedPlan);

  const isWorryFreeEnabled =
    siteConfigurations && siteConfigurations.switches.DETOX_WORRY_FREE;

  const {
    isFeatureAllowed: [
      abTestingLineSelectionEnabled,
      abTestingEshopCreateOrder
    ],
    isPending: abTestApiPending
  } = useABTesting({
    featureName: [
      AbFeatureNames.LINE_SELECTION,
      AbFeatureNames.ESHOP_CREATE_ORDER
    ]
  });

  const hasUserInfo = Boolean(userInformation);
  const isUserLoggedIn = Boolean(
    hasUserInfo && !userInformation?.anonymousUser
  );
  const isUserCis = Boolean(cisInformation?.$myInfo);
  const isCisUserLoginTypeMyInfo =
    isUserCis && Boolean(cisInformation?.$myInfo);

  useSpecialPromo({
    plans,
    aemVariableConfigs
  });

  /* Clear user data on first load. */
  useEffect(() => {
    resetUser();
    const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
    if (uiamAccessToken && !isUserLoggedIn) {
      getUserInformation();
    }
  }, []);

  useEffect(() => {
    if (!userMyInfo) return;
    setMyInfoData({
      idNumber: userMyInfo.customerId,
      mobileNumber: userMyInfo.contactNumber,
      passType: userMyInfo.passType
    });
  }, [userMyInfo]);

  // CIS Customer session flow
  useEffect(() => {
    // If logged in, send a Clicktale event
    if (userInformation && userInformation.clientContext) {
      userInformation.clientContext.customers.forEach(customer => {
        ctSendEvent('CustomerId', customer.customerId);
        dtIdentifyUser(customer.customerId);
      });
    }
  }, [isCisUserLoginTypeMyInfo]);

  const isLoading =
    isUserInfoLoading || isMyInfoLoading || abTestApiPending === true;

  return (
    <>
      <Auth
        loading={isLoading}
        key={JSON.stringify(myInfoData)}
        isAuthenticated={isUserLoggedIn}
        authStatus={authStatus}
        setAuthStatus={setAuthStatus}
        isWorryFreeEnabled={!isMobileShareFlow && isWorryFreeEnabled}
        worryFreeInitialFormData={myInfoData}
        onePassTargetUrl={navigation.CHOOSE_NUMBER_PAGE}
      >
        {
          <ChooseYourNumber
            data={data}
            abTestingLineSelectionEnabled={abTestingLineSelectionEnabled}
            abTestingEshopCreateOrder={abTestingEshopCreateOrder}
          />
        }
      </Auth>
    </>
  );
};

ChooseYourNumberPage.propTypes = {
  authStatus: PropTypes.string,
  userInformation: PropTypes.object,
  setAuthStatus: PropTypes.func,
  getCISMyInfo: PropTypes.func
};

/* istanbul ignore next */
const mapStateToProps = state => {
  return {
    authStatus: state.auth.status,
    userInformation: state.user.information,
    cisInformation: state.user.cis.information,
    userMyInfo: state.user.cis.information?.cusMyInfo,
    selectedPlan: state.plan.selectedPlan,
    userLoading: state.user?.loading
  };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => {
  const { setAuthStatus, getCISMyInfo } = authActions;
  const { getUserInformation, resetUser } = userActions;
  return {
    setAuthStatus: status => dispatch(setAuthStatus(status)),
    getCISMyInfo: params => dispatch(getCISMyInfo(params)),
    resetUser: () => dispatch(resetUser()),
    getUserInformation: options => dispatch(getUserInformation(options))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChooseYourNumberPage);

export const query = graphql`
  {
    allPlan {
      edges {
        node {
          ...PlanDetailFragment
        }
      }
    }
    allAemPages(filter: { pageUrl: { eq: "/eshop/admin.xjson" } }) {
      edges {
        node {
          title
          components {
            ...AemComponentsFragment
          }
        }
      }
    }
    allVariableConfig {
      edges {
        node {
          name
          value
        }
      }
    }
    allAddonPreTick {
      edges {
        node {
          boIds
          id
          phoneList
          phoneSpecific
          planIds
          segment
          spsIds
          transactionBoIds {
            gaBoIds
            portinBoIds
            reconBoIds
          }
          transactionSpsIds {
            gaSpsIds
            portinSpsIds
            reconSpsIds
          }
          transactionSpecific
          type
        }
      }
    }
    allChooseSimOptions {
      edges {
        node {
          notAvailable
          footerNotAvailable
          simTitle
          simTypeId
          simDetails {
            transactionType
            descWithDevice
            descWithoutDevice
            footerWithDevice
            footerWithoutDevice
            simPrice
          }
        }
      }
    }
    allAddon {
      edges {
        node {
          flowType
          contract
          groupName
          id
          onetimePrice
          phoneList
          price
          productDescription
          productSpecContainmentID
          productTitle
          ranking
          salesRanking
          atomic
          type
          promotionText
          requiredDevice
          addonPopupTitle
          addonPopupMessage
          addonPopupButtonLabel
          categoryId
          categorySequence
        }
      }
    }
    addonSimOnlyPlan {
      data {
        addonId
        price
        quantity
        unit
      }
      sms {
        addonId
        price
        quantity
        unit
      }
      talktime {
        addonId
        price
        quantity
        unit
      }
    }
    allPhone(filter: { brand: { eq: "Apple" } }) {
      edges {
        node {
          id
          groupId
          productId
          variants {
            productId
          }
        }
      }
    }
    allVariableConfig {
      edges {
        node {
          name
          value
        }
      }
    }
  }
`;
